import React, { useState } from 'react';
import './App.css';
import { ClerkProvider, SignedIn, SignedOut, useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './components/ThemeProvider';
import Sidebar from './components/Sidebar';
import TaskList from './components/TaskList';
import TaskDetail from './components/TaskDetail';
import LoginPage from './components/LoginPage';
import { Loader2 } from 'lucide-react';

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable');
}

const AppLayout = () => {
  const { selectedTask, setSelectedTaskId, loading } = useApp();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const handleCloseDetail = () => {
    setSelectedTaskId(null);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Mobile overlay */}
      {(sidebarOpen || selectedTask) && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => {
            setSidebarOpen(false);
            if (selectedTask) setSelectedTaskId(null);
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <Sidebar onClose={handleCloseSidebar} user={user} onLogout={logout} />
      </div>

      {/* Main content */}
      <TaskList onMenuClick={() => setSidebarOpen(true)} />

      {/* Task Detail */}
      {selectedTask && (
        <div
          className={`fixed lg:static inset-y-0 right-0 z-40 transform transition-transform duration-300 ease-in-out
            ${selectedTask ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <TaskDetail onClose={handleCloseDetail} />
        </div>
      )}
    </div>
  );
};

const AuthenticatedApp = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <AppProvider>
      <AppLayout />
    </AppProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </ClerkProvider>
    </ThemeProvider>
  );
}

export default App;
